import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, type AdminSandbox } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Pause, Play, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type SandboxState = "running" | "paused";
const EXTEND_MS = 15 * 60 * 1000;

function SandboxActions({
  containerId,
  state,
  sb,
}: {
  containerId: string;
  state: SandboxState;
  sb: AdminSandbox;
}) {
  const queryClient = useQueryClient();
  // Refetch just this tab (kill/extend keep the sandbox in the same state)…
  const invalidateState = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-sandboxes-page", containerId, state] });
  // …vs both tabs: pause/resume move the sandbox between running and paused, so
  // the other tab's cache goes stale too (prefix match invalidates every state).
  const invalidateAll = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-sandboxes-page", containerId] });

  const opts = (verb: string, invalidate: () => void) => ({
    onSuccess: () => {
      toast.success(`Sandbox ${verb}`);
      invalidate();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : `Failed to ${verb} sandbox`),
  });

  const kill = useMutation({ mutationFn: () => api.admin.killSandbox(containerId, sb.sandboxId), ...opts("killed", invalidateState) });
  const pause = useMutation({ mutationFn: () => api.admin.pauseSandbox(containerId, sb.sandboxId), ...opts("paused", invalidateAll) });
  const resume = useMutation({ mutationFn: () => api.admin.resumeSandbox(containerId, sb.sandboxId), ...opts("resumed", invalidateAll) });
  const extend = useMutation({
    mutationFn: () => api.admin.setSandboxTimeout(containerId, sb.sandboxId, EXTEND_MS),
    onSuccess: () => {
      toast.success("Timeout extended to 15m from now");
      invalidateState();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to extend timeout"),
  });
  const busy = kill.isPending || pause.isPending || resume.isPending || extend.isPending;

  return (
    <div className="flex items-center justify-end gap-1.5">
      {sb.state === "running" ? (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => pause.mutate()}>
          <Pause className="size-3 mr-1" />
          Pause
        </Button>
      ) : (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => resume.mutate()}>
          <Play className="size-3 mr-1" />
          Resume
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={busy || sb.state !== "running"}
        onClick={() => extend.mutate()}
        title={
          sb.state === "running"
            ? "Reset the idle timeout to 15 minutes from now"
            : "Only available for running sandboxes"
        }
      >
        <Clock className="size-3 mr-1" />
        Extend 15m
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" disabled={busy}>
            <Trash2 className="size-3 mr-1" />
            Kill
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kill this sandbox?</AlertDialogTitle>
            <AlertDialogDescription>
              Immediately terminates sandbox <span className="font-mono">{sb.sandboxId}</span>. Any
              in-progress work in the current turn is lost. Boo provisions a fresh sandbox on the
              next turn. <strong>This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => kill.mutate()}>Kill sandbox</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// The exhaustive, filterable sandboxes view — running/paused tabs, provider +
// thread filters, cursor "Load more". Rendered inside the container page's
// Sandboxes tab.
export function SandboxesPanel({
  containerId,
  initialState = "running",
}: {
  containerId: string;
  initialState?: SandboxState;
}) {
  const [state, setState] = useState<SandboxState>(initialState);
  const [providerFilter, setProviderFilter] = useState("");
  const [threadFilter, setThreadFilter] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["admin-sandboxes-page", containerId, state],
    queryFn: ({ pageParam }) => api.admin.containerSandboxesPage(containerId, state, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const loaded = query.data?.pages.flatMap((p) => p.sandboxes) ?? [];
  const provider = providerFilter.trim().toLowerCase();
  const thread = threadFilter.trim().toLowerCase();
  const rows = loaded.filter(
    (s) =>
      (!provider || (s.provider ?? "").toLowerCase().includes(provider)) &&
      (!thread || (s.threadId ?? "").toLowerCase().includes(thread)),
  );
  const filtering = provider !== "" || thread !== "";

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border p-0.5">
          {(["running", "paused"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setState(s)}
              className={`rounded px-3 py-1 text-sm capitalize transition-colors ${
                state === s ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <Input
          placeholder="Filter by provider"
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="h-9 w-48"
        />
        <Input
          placeholder="Filter by thread"
          value={threadFilter}
          onChange={(e) => setThreadFilter(e.target.value)}
          className="h-9 w-64"
        />
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto gap-1"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          <RefreshCw className={`size-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <LoadingIndicator />
      ) : query.isError ? (
        <ErrorComponent error={query.error ?? "Failed to load sandboxes"} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          {loaded.length === 0
            ? `No ${state} sandboxes.`
            : "No sandboxes match the filter on the loaded pages — load more to keep searching."}
        </p>
      ) : (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 font-medium">Sandbox</th>
                <th className="px-4 py-2 font-medium">Provider / thread</th>
                <th className="px-4 py-2 font-medium">Lifetime</th>
                <th className="px-4 py-2 font-medium">Resources</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((sb) => (
                <tr key={sb.sandboxId} className="align-top">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className={`inline-block size-2 rounded-full ${sb.state === "running" ? "bg-green-500" : "bg-amber-500"}`}
                      />
                      {sb.state}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs break-all">{sb.sandboxId}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{sb.provider ?? "—"}</div>
                    <div className="text-muted-foreground break-all">{sb.threadId ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    <div>Started {relativeIso(sb.startedAt)}</div>
                    <div>Expires {relativeIso(sb.endAt)}</div>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    <div>
                      {sb.cpuCount} vCPU · {sb.memoryMB} MB
                    </div>
                    <div className="text-muted-foreground">
                      {sb.state === "running" && sb.cpuUsedPct !== null
                        ? `${sb.cpuUsedPct.toFixed(0)}% CPU${sb.memUsedMB !== null ? ` · ${Math.round(sb.memUsedMB)} MB used` : ""}`
                        : "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <SandboxActions containerId={containerId} state={state} sb={sb} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          {filtering
            ? `${rows.length} shown · ${loaded.length} ${state} loaded`
            : `${loaded.length} ${state} loaded`}
        </span>
        {query.hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
        {!query.hasNextPage && loaded.length > 0 && <span>End of list.</span>}
      </div>
    </div>
  );
}
